import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { selectTodaySession } from '../../slices/todaySession/todaySessionSlice';
import { clearPastFlashcards } from '../../slices/pastFlashcards/pastFlashcardsSlice';
import { X } from 'react-feather';
import { RootState } from '../../store/store';
import { useHotkeys } from 'react-hotkeys-hook';
import { useRefreshStudySessionAndContent } from '../../utils/refreshStudySession';
import { getUserInfo, updateCurrentUserInfo } from '../../services/auth';
import moment from 'moment';
import { enqueueSnackbar } from 'notistack';
import AppBar from '../../components/Global/AppBar';
import HomeFeatureTour from '../../components/Home/HomeFeatureTour';
import useWindowWidth from '../../hooks/useWindowWidth';
import { useGamepadContext } from '../../context/useGamepadContext';
import { getAssentsToAccept, getLatestAssents } from '../../services/assent';
import { AssentType } from '../../types/Assent';
import AssentsModal from '../../components/Home/AssentsModal';
import NavBar from '../../components/Global/NavBar';
import AppPromo from '../../assets/images/app-promo.png';
import QRCode from '../../assets/ora-qr.svg';
import CustomModal from '../../components/Global/CustomModal';
// import { getLearningObjectivesForToday } from '../../services/vignettes';
import { Capacitor, PluginListenerHandle } from '@capacitor/core';
import { examsSelectors } from '../../slices/exams/examsSlice';
import { getUserSchool } from '../../services/users';
import OraFeedbackModal from '../../components/Global/OraFeedback';
import { FeedbackTrigger } from '../../types/Feedback';
import HomeHeader from '../../components/Home/HomeHeader';
import TodaySession from '../../components/Home/TodaySession';
import { useStudyPhaseSend } from '../../studyPhase';
import FlashLoader from '../../components/FlashLoader';
import { MINIMUM_START_DATE } from '../../utils/tourHelpers';
import HomeFeatureTourPart2 from '../../components/Home/HomeFeatureTourPart2';
import HomeFeatureTourPart3 from '../../components/Home/HomeFeatureTourPart3';
import { App } from '@capacitor/app';
import { useRctSelector } from '../../hooks/useRctSelector';
import EditableLayout from '../../components/Home/EditableLayout';
import RctTimeline from '../../assets/rct/rct-timeline.png';

// Screensaver
// import useIdle from '../../utils/useIdle';
// import { createPortal } from 'react-dom';
// import Screensaver from './Screensaver';

interface HomeProps {}

const Home: React.FC<HomeProps> = () => {
  const todaySession = useSelector(selectTodaySession);
  const primaryExam = useSelector(examsSelectors.selectPrimaryExam);
  const currentUser = useSelector((state: RootState) => state.user.profile);
  const subscription = useSelector((state: RootState) => state.subscription);
  const startedSessionCount = useSelector(
    (state: RootState) => state.stats.startedStudySessions
  );

  const [isNoContent, setIsNoContent] = useState(false);
  const [showAppPromo, setShowAppPromo] = useState(false);
  const [justShowedTip, setJustShowedTip] = useState(false);
  const [showQrCodeModal, setShowQrCodeModal] = useState(false);
  const [showHomeTour, setShowHomeTour] = useState(false);
  const [showHomeTourPart2, setShowHomeTourPart2] = useState(false);
  const [showHomeTourPart3, setShowHomeTourPart3] = useState(false);

  const [oraFeedbackModalIsOpen, setOraFeedbackModalIsOpen] = useState(false);
  const [feedbackTrigger, setFeedbackTrigger] =
    useState<FeedbackTrigger | null>(null);

  // const [screenSaverDisplay, setScreenSaverDisplay] = useState('none');
  // const [learningObjectives, setLearningObjectives] = useState<string[]>([]);
  const [assentsToAccept, setAssentsToAccept] = useState<
    { type: AssentType; version: string }[]
  >([]);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { sendPhaseEvent } = useStudyPhaseSend();

  const {
    isLoadedStudySessionAndContent,
    isLoadingStudySessionAndContent,
    setIsLoadingStudySessionAndContent,
    setIsLoadedStudySessionAndContent,
    refreshStudySessionAndContent,
  } = useRefreshStudySessionAndContent();

  const refreshHomePage = async () => {
    await setIsLoadingStudySessionAndContent(true);
    await setIsLoadedStudySessionAndContent(false);
    await refreshStudySessionAndContent();
    await setIsLoadingStudySessionAndContent(false);
    await setIsLoadedStudySessionAndContent(true);
  };

  const { renderRctSelector } = useRctSelector({
    refreshHomePage,
  });

  const windowWidth = useWindowWidth();

  // const { isIdle } = useIdle(
  //   300000,
  //   isLoadedStudySessionAndContent &&
  //     currentUser?.screensaverPreference !== 'none'
  // );

  useHotkeys('d', () => updateDemoMode(!currentUser?.demoMode), {
    keyup: true,
  });

  useEffect(() => {
    getUserInfo();
  }, []);

  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      let stateChangeListener: PluginListenerHandle;

      const setupListeners = async () => {
        stateChangeListener = await App.addListener(
          'appStateChange',
          ({ isActive }) => {
            if (isActive) {
              refreshHomePage();
            }
          }
        );
      };

      setupListeners();

      return () => {
        stateChangeListener?.remove();
      };
    }
  }, []);

  // Gamepad
  const { lastButtonEvent } = useGamepadContext();
  useEffect(() => {
    if (lastButtonEvent && lastButtonEvent.pressed) {
      const { universalButton } = lastButtonEvent;
      if (!universalButton) {
        return;
      }
      switch (universalButton) {
        case 'A':
          sendPhaseEvent({ type: 'START_SESSION_GENERIC' });
          break;
        default:
          break;
      }
    }
  }, [lastButtonEvent]);

  useEffect(() => {
    dispatch(clearPastFlashcards());

    determineAssentStatuses();

    sessionStorage.removeItem('ora-refId');
    sessionStorage.removeItem('ora-cohort');

    if (currentUser?.isOnboarded === false) {
      navigate('/onboarding');
      return;
    }

    const params = new URLSearchParams(window.location.search);
    if (params.get('delete-request') === 'true') {
      navigate('/account');
    }

    if (currentUser?.isOnboarded === true && !isLoadedStudySessionAndContent) {
      refreshStudySessionAndContent();
    }

    if (currentUser) {
      getUserSchool(currentUser.id);
    }
  }, [currentUser?.isOnboarded, currentUser?.id]);

  useEffect(() => {
    if (currentUser && startedSessionCount) {
      determineFeedbackModal(startedSessionCount);
    }
  }, [currentUser, startedSessionCount]);

  // useEffect(() => {
  //   if (isIdle) {
  //     setScreenSaverDisplay('flex');
  //   } else {
  //     setTimeout(() => {
  //       setScreenSaverDisplay('none');
  //     }, 1000);
  //   }
  // }, [isIdle]);

  useEffect(() => {
    if (isLoadingStudySessionAndContent) return;

    setIsNoContent(false);
    if (todaySession && todaySession.id && primaryExam) {
      if (todaySession.isComplete) return;
      console.log('Checking if No Content Available for Today');
      if (primaryExam.isUsingFlashcards && !primaryExam.isUsingQbank) {
        setIsNoContent(todaySession.flashcardCount === 0);
      }
      if (primaryExam.isUsingQbank && !primaryExam.isUsingFlashcards) {
        setIsNoContent(todaySession.vignetteCount === 0);
      }
      if (primaryExam.isUsingFlashcards && primaryExam.isUsingQbank) {
        setIsNoContent(
          todaySession.flashcardCount === 0 && todaySession.vignetteCount === 0
        );
      }
    }
  }, [todaySession, primaryExam, isLoadingStudySessionAndContent]);

  // Handle Onboarding Tips
  useEffect(() => {
    const homeTourStartDate = MINIMUM_START_DATE;

    if (!currentUser?.ambassador) {
      // Home Tour Part 1
      if (currentUser?.createdAt) {
        const userCreatedAt = moment.utc(currentUser.createdAt);
        if (userCreatedAt.isAfter(homeTourStartDate)) {
          // Prevent showing tour to existing users
          if (currentUser?.onboardingTips) {
            if (!currentUser.onboardingTips['home-tour']) {
              setTimeout(() => {
                setShowHomeTour(true);
                setJustShowedTip(true);
              }, 750);
            }
          }
        }
      }
      // Home Tour Part 2
      if (currentUser?.createdAt) {
        const userCreatedAt = moment.utc(currentUser.createdAt);
        if (
          todaySession?.isStarted &&
          moment(todaySession?.date).isSame(userCreatedAt, 'day') &&
          userCreatedAt.isAfter(homeTourStartDate)
        ) {
          if (currentUser?.onboardingTips) {
            if (!currentUser.onboardingTips['home-tour-part-2']) {
              setTimeout(() => {
                setShowHomeTourPart2(true);
                setJustShowedTip(true);
              }, 750);
            }
          }
        }
      }
      // Home Tour Part 3
      if (currentUser?.createdAt) {
        const userCreatedAt = moment.utc(currentUser.createdAt);
        if (
          todaySession?.isStarted &&
          todaySession?.isComplete &&
          moment(todaySession?.date).isSame(userCreatedAt, 'day') &&
          userCreatedAt.isAfter(homeTourStartDate)
        ) {
          if (currentUser?.onboardingTips) {
            if (!currentUser.onboardingTips['home-tour-part-3']) {
              setTimeout(() => {
                setShowHomeTourPart3(true);
                setJustShowedTip(true);
              }, 1250);
            }
          }
        }
      }
    }

    // App Promo
    if (
      currentUser &&
      !currentUser?.onboardingTips['app-promo'] &&
      ((currentUser.onboardingTips['home-tour'] &&
        currentUser.onboardingTips['home-tour-part-2'] &&
        currentUser.onboardingTips['home-tour-part-3']) ||
        currentUser.onboardingTips['ambassador-tour']) &&
      !(currentUser?.hasIOSApp || currentUser?.hasAndroidApp) &&
      !justShowedTip &&
      !Capacitor.isNativePlatform() &&
      windowWidth > 900
    ) {
      setShowAppPromo(true);
    }
  }, [
    todaySession,
    currentUser?.createdAt,
    currentUser?.onboardingTips,
    windowWidth,
    justShowedTip,
    subscription?.subscription?.status,
  ]);

  const determineFeedbackModal = (startedSessionCount: number) => {
    if (
      startedSessionCount >= 50 &&
      currentUser?.lastFeedbackTrigger !== FeedbackTrigger.SESSIONS_50
    ) {
      setFeedbackTrigger(FeedbackTrigger.SESSIONS_50);
      setOraFeedbackModalIsOpen(true);
    } else if (
      startedSessionCount >= 20 &&
      currentUser?.lastFeedbackTrigger !== FeedbackTrigger.SESSIONS_20 &&
      currentUser?.lastFeedbackTrigger !== FeedbackTrigger.SESSIONS_50
    ) {
      setFeedbackTrigger(FeedbackTrigger.SESSIONS_20);
      setOraFeedbackModalIsOpen(true);
    } else if (
      startedSessionCount >= 5 &&
      currentUser?.lastFeedbackTrigger !== FeedbackTrigger.SESSIONS_5 &&
      currentUser?.lastFeedbackTrigger !== FeedbackTrigger.SESSIONS_20 &&
      currentUser?.lastFeedbackTrigger !== FeedbackTrigger.SESSIONS_50
    ) {
      setFeedbackTrigger(FeedbackTrigger.SESSIONS_5);
      setOraFeedbackModalIsOpen(true);
    }
  };

  const determineAssentStatuses = async () => {
    if (!currentUser) return;
    if (currentUser.cohort === 'rct') return;
    const latestAssents = await getLatestAssents(currentUser.id);

    if (latestAssents) {
      const assentsToAccept = getAssentsToAccept(latestAssents);
      if (assentsToAccept.length > 0) {
        setAssentsToAccept(assentsToAccept);
      } else {
        console.log('User has accepted all the latest policies.');
      }
    } else {
      console.error('Failed to fetch latest assents.');
    }
  };

  const updateDemoMode = async (demoMode: boolean) => {
    if (currentUser?.role !== 'admin') return;
    await updateCurrentUserInfo({ demoMode });
    enqueueSnackbar(demoMode ? 'Demo Mode Enabled' : 'Demo Mode Disabled', {
      autoHideDuration: 3000,
    });
  };

  // const fetchLearningObjectives = async () => {
  //   if (!currentUser) return;
  //   const learningObjectives = await getLearningObjectivesForToday(
  //     currentUser.id
  //   );
  //   if (learningObjectives) {
  //     setLearningObjectives(learningObjectives);
  //   }
  // };

  const renderAppPromo = (show: boolean) => {
    if (!show) return null;
    return (
      <>
        <div
          className="app-promo"
          onClick={() => {
            setShowQrCodeModal(true);
          }}
        >
          <div className="app-promo_header">
            <h2>Download the Ora App</h2>
            <div style={{ alignSelf: 'baseline' }}>
              <button
                className="button button--icon-only button--close"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowAppPromo(false);
                  handleSeenMessage('app-promo', true);
                }}
                aria-label="Close app promotion"
              >
                <X />
              </button>
            </div>
          </div>
          <div className="app-promo_body">
            <div>
              <img src={AppPromo} alt="Download Ora Mobile Apps" />
            </div>
            <div className="qr">
              <img src={QRCode} alt="Ora QR Code" />
            </div>
          </div>
        </div>
        <CustomModal
          open={showQrCodeModal}
          onClose={() => {
            setShowQrCodeModal(false);
          }}
          name={'qr-code'}
        >
          <img src={QRCode} alt="Ora QR Code" />
        </CustomModal>
      </>
    );
  };

  const handleSeenMessage = async (
    messageId: string,
    activateJustShowedTip: boolean
  ) => {
    if (!currentUser) return;
    if (activateJustShowedTip) {
      setJustShowedTip(true);
    }
    await updateCurrentUserInfo({
      onboardingTips: {
        ...currentUser?.onboardingTips,
        [messageId]: true,
      },
    });
  };

  return (
    <div className="index-container">
      <div className="home">
        <NavBar />
        <div className="home_body">
          <div className="sc-home">
            <HomeHeader
              setIsLoadedStudySessionAndContent={
                setIsLoadedStudySessionAndContent
              }
              refreshStudySessionAndContent={refreshStudySessionAndContent}
              setIsLoadingStudySessionAndContent={
                setIsLoadingStudySessionAndContent
              }
            />
            <div className="sc-home_main">
              <div className="sc-home_main_session">
                <TodaySession isNoContent={isNoContent} />
              </div>
              {primaryExam?.isRCT ? (
                <div className="rct-timeline">
                  <img src={RctTimeline} alt="RCT Timeline" />
                </div>
              ) : (
                <div className="sc-home_main_shelf">
                  <EditableLayout />
                </div>
              )}
            </div>
          </div>
          {renderAppPromo(showAppPromo)}
        </div>
        <AppBar />
      </div>
      {showHomeTour && currentUser?.cohort !== 'rct' && <HomeFeatureTour />}
      {showHomeTourPart2 && currentUser?.cohort !== 'rct' && (
        <HomeFeatureTourPart2 />
      )}
      {showHomeTourPart3 && currentUser?.cohort !== 'rct' && (
        <HomeFeatureTourPart3 />
      )}
      {renderRctSelector()}
      <AssentsModal
        currentUser={currentUser}
        assentsToAccept={assentsToAccept}
        setAssentsToAccept={setAssentsToAccept}
      />
      {oraFeedbackModalIsOpen && (
        <OraFeedbackModal
          trigger={feedbackTrigger}
          setOraFeedbackModalIsOpen={setOraFeedbackModalIsOpen}
        />
      )}
      {/* {windowWidth > 900 &&
        createPortal(
          <Screensaver
            isIdle={isIdle}
            screenSaverDisplay={screenSaverDisplay}
            learningObjectives={learningObjectives}
          />,
          document.body
        )} */}
      <FlashLoader visible={!isLoadedStudySessionAndContent} />
    </div>
  );
};

export default Home;
